/**
 * PipelineStatsRow — colorful "morning glance" KPI strip on the V3 Dashboard.
 *
 * Renders 4 tiles across the top of the dashboard, each with:
 *   · A rounded coloured icon well (blue / amber / purple / emerald)
 *   · A big tabular-nums count pulled live from `/api/v3/properties`
 *   · A stage label
 *   · A subtle delta pill (green when up, rose when down) computed from the
 *     workspace's KPI-history snapshot so nothing is fake.
 *
 * Inspired by the AI-redesign mockup Tracy shared 2026-09-09. Kept on the
 * warm-light-executive palette (no purple/blue gradients on white — those
 * are the AI-slop tells) but borrows the "colored icon + big number" pop
 * pattern that makes a dashboard feel alive at first glance.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ROLE } from "@/lib/palette";
import { Users, Sparkles, Handshake, Trophy, ArrowUpRight, ArrowDownRight } from "lucide-react";


// Which stages map into which "morning glance" bucket.
// Kept broad so any near-synonym pipeline stage still counts.
const STAGE_BUCKETS = {
  total_leads:     { include: ["lead", "new", "captured", "cold"] },
  under_analysis:  { include: ["analyzing", "analysis", "review", "reviewing", "researching", "warm"] },
  in_negotiation:  { include: ["negotiating", "negotiation", "offer_made", "counteroffer", "hot", "under_contract", "pending"] },
  closed_deals:    { include: ["closed", "won", "assigned", "sold", "settled"] },
};


const TILES = [
  { key: "total_leads",    label: "Total Leads",    icon: Users,     linkTo: "/v3/crm/leads",    role: "cold" },
  { key: "under_analysis", label: "Under Analysis", icon: Sparkles,  linkTo: "/v3/crm/pipeline", role: "warm" },
  { key: "in_negotiation", label: "In Negotiation", icon: Handshake, linkTo: "/v3/crm/pipeline", role: "hot"  },
  { key: "closed_deals",   label: "Closed Deals",   icon: Trophy,    linkTo: "/v3/closings",     role: "won"  },
];


// Turn an arbitrary Mongo stage string into a bucket key. Falls back to
// "total_leads" when we don't recognise it — so ghost stages don't get lost.
function bucketFor(stage) {
  const s = String(stage || "").toLowerCase().replace(/[\s-]+/g, "_");
  for (const [bucket, cfg] of Object.entries(STAGE_BUCKETS)) {
    if (cfg.include.some((token) => s === token || s.includes(token))) {
      return bucket;
    }
  }
  return null;
}


function DeltaPill({ pct }) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums
                  ${up ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`}
      data-testid="pipeline-stats-delta"
    >
      <Icon className="w-2.5 h-2.5" />
      {up ? "+" : ""}{pct}%
    </span>
  );
}


function Tile({ tile, count, delta, loading }) {
  const Icon = tile.icon;
  const c = ROLE[tile.role] || ROLE.info;
  return (
    <Link
      to={tile.linkTo}
      data-testid={`pipeline-stat-tile-${tile.key}`}
      className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md
                 transition-all hover:-translate-y-0.5 hover:border-slate-300 relative overflow-hidden"
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: c.dot }} />
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
             style={{ background: c.bg }}>
          <Icon className="w-5 h-5" style={{ color: c.fg }} />
        </div>
        <DeltaPill pct={delta} />
      </div>
      <div className="mt-4 text-3xl font-black tracking-tight text-slate-900 tabular-nums"
           data-testid={`pipeline-stat-value-${tile.key}`}>
        {loading ? (
          <span className="inline-block w-10 h-7 rounded bg-slate-100 animate-pulse" />
        ) : (
          count.toLocaleString()
        )}
      </div>
      <div className="mt-1 text-xs uppercase tracking-widest text-slate-500 font-bold">
        {tile.label}
      </div>
    </Link>
  );
}


export default function PipelineStatsRow() {
  const [state, setState] = useState({
    loading: true,
    counts:  { total_leads: 0, under_analysis: 0, in_negotiation: 0, closed_deals: 0 },
    deltas:  { total_leads: null, under_analysis: null, in_negotiation: null, closed_deals: null },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer the dedicated /dashboard/pipeline-stats endpoint — it
        // returns counts + honest week-over-week deltas in one call.
        const { data } = await api.get("/v3/dashboard/pipeline-stats");
        if (cancelled) return;
        setState({
          loading: false,
          counts: data?.counts || { total_leads: 0, under_analysis: 0, in_negotiation: 0, closed_deals: 0 },
          deltas: data?.deltas || { total_leads: null, under_analysis: null, in_negotiation: null, closed_deals: null },
        });
      } catch {
        // Fallback: derive from the properties collection so the strip
        // still renders even if the stats endpoint is unreachable.
        try {
          const { data } = await api.get("/v3/properties?limit=500");
          const items = data?.items || data || [];
          const counts = { total_leads: 0, under_analysis: 0, in_negotiation: 0, closed_deals: 0 };
          for (const p of items) {
            const b = bucketFor(p.stage);
            if (b) counts[b]++;
          }
          counts.total_leads = items.length;
          if (!cancelled) setState((s) => ({ ...s, loading: false, counts }));
        } catch {
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tiles = useMemo(() => TILES, []);

  return (
    <section
      data-testid="pipeline-stats-row"
      className="grid gap-4 grid-cols-2 lg:grid-cols-4"
    >
      {tiles.map((t) => (
        <Tile
          key={t.key}
          tile={t}
          count={state.counts[t.key] || 0}
          delta={state.deltas[t.key]}
          loading={state.loading}
        />
      ))}
    </section>
  );
}
